import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Button,
  Typography,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Alert,
  CircularProgress,
  Divider
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { handleError } from "../../utils/lib";
import { useSnackbar } from "../../providers/SnackBarProvider";
import { useAppBar } from "../../providers/AppBarProvider";
import { useTranslation } from 'react-i18next';
import EditPage from "./EditPage";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB en bytes

// Enum para las columnas posibles
const COLUMN_OPTIONS = [
  { value: "NA", label: "N/A" },
  { value: "A", label: "A" },
  { value: "B", label: "B" },
  { value: "C", label: "C" },
  { value: "D", label: "D" },
  { value: "E", label: "E" },
  { value: "F", label: "F" },
  { value: "G", label: "G" },
  { value: "H", label: "H" },
  { value: "I", label: "I" },
  { value: "J", label: "J" },
  { value: "K", label: "K" },
  { value: "L", label: "L" },
  { value: "M", label: "M" },
  { value: "N", label: "N" },
  { value: "O", label: "O" },
  { value: "P", label: "P" },
  { value: "Q", label: "Q" },
  { value: "R", label: "R" },
  { value: "S", label: "S" },
  { value: "T", label: "T" },
  { value: "U", label: "U" },
  { value: "V", label: "V" },
  { value: "W", label: "W" },
  { value: "X", label: "X" },
  { value: "Y", label: "Y" },
  { value: "Z", label: "Z" }
];

const EditImport = () => {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { updateTitle, toggleFab } = useAppBar();
  const { t } = useTranslation();
  
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [fileIdentifier, setFileIdentifier] = useState(null);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  // Estado para las filas de inicio y fin
  const [startRow, setStartRow] = useState("3");
  const [endRow, setEndRow] = useState("");

  // Estado para las columnas
  const [columnMappings, setColumnMappings] = useState({
    nameColumn: "B",
    barCodeColumn: "A",
    categoryColumn: "C",
    unitColumn: "D",
    complexQuantityColumn: "E",
    costColumn: "F",
    taxColumn: "G",
    minQuantityColumn: "H",
    expiresColumn: "I",
    fixedPrice1Column: "J",
    priceFormat1Column: "K",
    margin1Column: "L",
    fixedPrice2Column: "M",
    priceFormat2Column: "N",
    margin2Column: "O",
    fixedPrice3Column: "P",
    priceFormat3Column: "Q",
    margin3Column: "R",
    resetQuantityColumn: "T",
    deleteRestoreColumn: "U"
  });

  // Validaciones de los campos
  const [errors, setErrors] = useState({});

  useEffect(() => {
    updateTitle(
      '',
      t('edit.importTitle'),
      t('edit.importSubtitle')
    );
    toggleFab(false);
  }, []);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    
    if (!selectedFile) {
      return;
    }
    
    // Validar que sea un archivo xlsx
    if (!selectedFile.name.endsWith('.xlsx')) {
      setError(t('edit.importInvalidFormat'));
      setFile(null);
      return;
    }
    
    // Validar tamaño máximo
    if (selectedFile.size > MAX_FILE_SIZE) {
      setError(t('edit.importFileTooLarge'));
      setFile(null);
      return;
    }
    
    setFile(selectedFile);
    setError("");
  };

  const handleUploadFile = async () => {
    if (!file) {
      setError(t('edit.importSelectFile'));
      return;
    }

    try {
      setIsUploading(true);
      
      // Convertir el archivo a base64
      const fileBase64 = await convertFileToBase64(file);
      
      // Crear objeto para enviar al servidor
      const uploadData = {
        file_base64: fileBase64
      };

      // Enviar el archivo al servidor
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}import_from_xlsx_1`,
        uploadData,
        { withCredentials: true }
      );

      if (response.data && response.data.data && response.data.data.file_identifier) {
        setFileIdentifier(response.data.data.file_identifier);
        snackbar(t('edit.importFileUploaded'));
      } else {
        throw new Error(t('edit.importInvalidId'));
      }
    } catch (error) {
      handleError(error, snackbar);
      setError(t('edit.importUploadError'));
    } finally {
      setIsUploading(false);
    }
  };

  // Función para convertir un archivo a base64
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Eliminar el prefijo "data:application/..." y quedarnos con la parte base64
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const handleColumnChange = (columnName, value) => {
    setColumnMappings({
      ...columnMappings,
      [columnName]: value
    });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!startRow || isNaN(parseInt(startRow)) || parseInt(startRow) <= 0) {
      newErrors.startRow = t('edit.importValidRow');
    }
    
    if (!endRow || isNaN(parseInt(endRow)) || parseInt(endRow) <= 0) {
      newErrors.endRow = t('edit.importValidRow');
    }
    
    if (parseInt(endRow) < parseInt(startRow)) {
      newErrors.endRow = t('edit.importEndRowGreater');
    }
    
    if (columnMappings.nameColumn === "NA") {
      newErrors.nameColumn = t('edit.importNameRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!fileIdentifier) {
      setError(t('edit.importUploadFirst'));
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Crear objeto con las correspondencias
      const mappingData = {
        file_identifier: fileIdentifier,
        start_row: parseInt(startRow),
        end_row: parseInt(endRow),
        
        // Agregar todas las correspondencias que no sean NA
        name_column: columnMappings.nameColumn,
        bar_code_column: columnMappings.barCodeColumn !== "NA" ? columnMappings.barCodeColumn : null,
        category_column: columnMappings.categoryColumn !== "NA" ? columnMappings.categoryColumn : null,
        unit_column: columnMappings.unitColumn !== "NA" ? columnMappings.unitColumn : null,
        complex_quantity_column: columnMappings.complexQuantityColumn !== "NA" ? columnMappings.complexQuantityColumn : null,
        cost_column: columnMappings.costColumn !== "NA" ? columnMappings.costColumn : null,
        tax_column: columnMappings.taxColumn !== "NA" ? columnMappings.taxColumn : null,
        min_quantity_column: columnMappings.minQuantityColumn !== "NA" ? columnMappings.minQuantityColumn : null,
        expires_column: columnMappings.expiresColumn !== "NA" ? columnMappings.expiresColumn : null,
        
        fixed_price_1_column: columnMappings.fixedPrice1Column !== "NA" ? columnMappings.fixedPrice1Column : null,
        format_price_1_column: columnMappings.priceFormat1Column !== "NA" ? columnMappings.priceFormat1Column : null,
        margin_price_1_column: columnMappings.margin1Column !== "NA" ? columnMappings.margin1Column : null,
        
        fixed_price_2_column: columnMappings.fixedPrice2Column !== "NA" ? columnMappings.fixedPrice2Column : null,
        format_price_2_column: columnMappings.priceFormat2Column !== "NA" ? columnMappings.priceFormat2Column : null,
        margin_price_2_column: columnMappings.margin2Column !== "NA" ? columnMappings.margin2Column : null,
        
        fixed_price_3_column: columnMappings.fixedPrice3Column !== "NA" ? columnMappings.fixedPrice3Column : null,
        format_price_3_column: columnMappings.priceFormat3Column !== "NA" ? columnMappings.priceFormat3Column : null,
        margin_price_3_column: columnMappings.margin3Column !== "NA" ? columnMappings.margin3Column : null,
        
        reset_quantity_column: columnMappings.resetQuantityColumn !== "NA" ? columnMappings.resetQuantityColumn : null,
        delete_restore_column: columnMappings.deleteRestoreColumn !== "NA" ? columnMappings.deleteRestoreColumn : null
      };
      
      //console.log(mappingData);
      
      // Enviar las correspondencias al servidor
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}import_from_xlsx_2`,
        mappingData,
        { withCredentials: true }
      );
      
      snackbar(t('edit.importSuccess'));
      navigate('/main');
    } catch (error) {
      handleError(error, snackbar);
      setError(t('edit.importProcessError'));
    } finally {
      setIsLoading(false);
    }
  };

  const renderColumnSelect = (columnName, label, currentValue, required = false) => {
    return (
      <Grid item xs={12} md={6}>
        <FormControl fullWidth error={required && errors[columnName]}>
          <InputLabel id={`${columnName}-label`}>{label}</InputLabel>
          <Select
            labelId={`${columnName}-label`}
            value={currentValue}
            label={label}
            onChange={(e) => handleColumnChange(columnName, e.target.value)}
          >
            {COLUMN_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {required && errors[columnName] && (
            <Typography variant="caption" color="error">
              {errors[columnName]}
            </Typography>
          )}
        </FormControl>
      </Grid>
    );
  };

  return (
    <EditPage>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          {t('edit.importFromExcel')}
        </Typography>
        
        <Box my={3}>
          <Typography variant="subtitle1" gutterBottom>
            {t('edit.importStep1')}
          </Typography>

          <Box
            component="div"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              p: 3,
              border: '1px dashed grey',
              borderRadius: 2,
              cursor: 'pointer',
              backgroundColor: '#f9f9f9',
              '&:hover': {
                backgroundColor: '#f0f0f0',
              },
              mb: 2
            }}
            onClick={() => fileInputRef.current.click()}
          >
            <input
              type="file"
              hidden
              ref={fileInputRef}
              accept=".xlsx"
              onChange={handleFileChange}
            />
            <CloudUploadIcon fontSize="large" color="primary" />
            <Typography variant="body1" mt={1}>
              {file ? file.name : t('edit.importClickToSelect')}
            </Typography>
            {file && (
              <Typography variant="caption" color="textSecondary">
                {t('edit.importFileSize', { size: (file.size / 1024 / 1024).toFixed(2) })}
              </Typography>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={handleUploadFile}
            disabled={isUploading || !file}
            startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isUploading ? t('edit.importUploading') : t('edit.importUploadFile')}
          </Button>
        </Box>

        <Divider sx={{ my: 3 }} />
        
        <Box my={3}>
          <Typography variant="subtitle1" gutterBottom>
            {t('edit.importStep2')}
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('edit.startRow')}
                value={startRow}
                onChange={(e) => setStartRow(e.target.value)}
                type="number"
                fullWidth
                error={!!errors.startRow}
                helperText={errors.startRow}
                disabled={!fileIdentifier}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('edit.endRow')}
                value={endRow}
                onChange={(e) => setEndRow(e.target.value)}
                type="number"
                fullWidth
                error={!!errors.endRow}
                helperText={errors.endRow}
                disabled={!fileIdentifier}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />
        
        <Box my={3}>
          <Typography variant="subtitle1" gutterBottom>
            {t('edit.importStep3')}
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            {t('edit.importColumnExplanation')}
          </Typography>
          
          <Grid container spacing={3}>
            {/* Campos principales */}
            {renderColumnSelect("nameColumn", t('edit.productNameColumn'), columnMappings.nameColumn, true)}
            {renderColumnSelect("barCodeColumn", t('edit.barCodeColumn'), columnMappings.barCodeColumn)}
            {renderColumnSelect("categoryColumn", t('edit.categoryColumn'), columnMappings.categoryColumn)}
            {renderColumnSelect("unitColumn", t('edit.unitColumn'), columnMappings.unitColumn)}
            {renderColumnSelect("complexQuantityColumn", t('edit.complexQuantityColumn'), columnMappings.complexQuantityColumn)}
            {renderColumnSelect("costColumn", t('edit.costColumn'), columnMappings.costColumn)}
            {renderColumnSelect("taxColumn", t('edit.taxColumn'), columnMappings.taxColumn)}
            {renderColumnSelect("minQuantityColumn", t('edit.minQuantityColumn'), columnMappings.minQuantityColumn)}
            {renderColumnSelect("expiresColumn", t('edit.expiresColumn'), columnMappings.expiresColumn)}
            {renderColumnSelect("resetQuantityColumn", t('edit.resetQuantityColumn'), columnMappings.resetQuantityColumn)}
            {renderColumnSelect("deleteRestoreColumn", t('edit.deleteRestoreColumn'), columnMappings.deleteRestoreColumn)}
            
            {/* Precio 1 */}
            <Grid item xs={12}>
              <Typography variant="subtitle2">{t('edit.priceN', { n: 1 })}</Typography>
            </Grid>
            {renderColumnSelect("fixedPrice1Column", t('edit.fixedPriceN', { n: 1 }), columnMappings.fixedPrice1Column)}
            {renderColumnSelect("priceFormat1Column", t('edit.calcFormat', { n: 1 }), columnMappings.priceFormat1Column)}
            {renderColumnSelect("margin1Column", t('edit.marginN', { n: 1 }), columnMappings.margin1Column)}
            
            {/* Precio 2 */}
            <Grid item xs={12}>
              <Typography variant="subtitle2">{t('edit.priceN', { n: 2 })}</Typography>
            </Grid>
            {renderColumnSelect("fixedPrice2Column", t('edit.fixedPriceN', { n: 2 }), columnMappings.fixedPrice2Column)}
            {renderColumnSelect("priceFormat2Column", t('edit.calcFormat', { n: 2 }), columnMappings.priceFormat2Column)}
            {renderColumnSelect("margin2Column", t('edit.marginN', { n: 2 }), columnMappings.margin2Column)}
            
            {/* Precio 3 */}
            <Grid item xs={12}>
              <Typography variant="subtitle2">{t('edit.priceN', { n: 3 })}</Typography>
            </Grid>
            {renderColumnSelect("fixedPrice3Column", t('edit.fixedPriceN', { n: 3 }), columnMappings.fixedPrice3Column)}
            {renderColumnSelect("priceFormat3Column", t('edit.calcFormat', { n: 3 }), columnMappings.priceFormat3Column)}
            {renderColumnSelect("margin3Column", t('edit.marginN', { n: 3 }), columnMappings.margin3Column)}
          </Grid>
        </Box>
        
        <Box mt={4} display="flex" justifyContent="center">
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSubmit}
            disabled={isLoading || !fileIdentifier}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isLoading ? t('edit.importProcessing') : t('edit.importButton')}
          </Button>
        </Box>
      </Paper>
    </EditPage>
  );
};

export default EditImport;
